// src/pages/ClientVerification.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../api/apiClient';
import logo from '../logo512.png';
import '../styles/Verification.css';

// Matches the mockup's own hand-drawn eye icon exactly, same as Login.
const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="#33565C" strokeWidth="1.5">
    <path d="M1.8 10S4.9 4.6 10 4.6 18.2 10 18.2 10 15.1 15.4 10 15.4 1.8 10 1.8 10z"></path>
    <circle cx="10" cy="10" r="2.4"></circle>
  </svg>
);

// Matches 6d's exact 4-requirement checklist + 4-segment strength bar.
function checkPasswordRules(password) {
  return {
    length: password.length >= 12,
    case: /[a-z]/.test(password) && /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
}

function PasswordStrength({ password }) {
  const rules = checkPasswordRules(password);
  const passedCount = Object.values(rules).filter(Boolean).length;

  return (
    <>
      <div className="pw-strength-bar">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`pw-strength-seg ${i < passedCount ? 'filled' : ''}`} />
        ))}
      </div>
      <div className="pw-rules-row">
        <span className={`pw-rule ${rules.length ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.length ? '✓' : ''}</span>12+ characters
        </span>
        <span className={`pw-rule ${rules.case ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.case ? '✓' : ''}</span>Upper &amp; lowercase
        </span>
        <span className={`pw-rule ${rules.number ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.number ? '✓' : ''}</span>A number
        </span>
        <span className={`pw-rule ${rules.symbol ? 'met' : ''}`}>
          <span className="pw-rule-check">{rules.symbol ? '✓' : ''}</span>A symbol
        </span>
      </div>
    </>
  );
}

function ClientVerification() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const type = searchParams.get('type') || 'client'; // 'client', 'user', or 'admin'
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    const [accountInfo, setAccountInfo] = useState(null);

    useEffect(() => {
        const handleEnterKey = (e) => {
            if (e.key === 'Enter' && !verifying && tokenValid && password && confirmPassword) {
                const form = e.target.closest('form');
                if (form) {
                    const submitEvent = new Event('submit', { cancelable: true });
                    form.dispatchEvent(submitEvent);
                }
            }
        };

        document.addEventListener('keydown', handleEnterKey);
        return () => {
            document.removeEventListener('keydown', handleEnterKey);
        };
    }, [verifying, tokenValid, password, confirmPassword]);

    useEffect(() => {
        const verifyToken = async () => {
            try {
                setLoading(true);
                const response = await apiFetchJSON(`/api/verify/check-token?token=${token}`);
                if (response.valid) {
                    setTokenValid(true);
                    setAccountInfo({
                        type: response.token_type,
                        // Only shown in the data block if the backend actually
                        // returns it - not fabricated when it doesn't.
                        username: response.username || null,
                    });
                } else {
                    setError('Invalid or expired verification link');
                }
            } catch (err) {
                setError(err.message || 'Failed to verify token');
            } finally {
                setLoading(false);
            }
        };
        
        if (token) {
            verifyToken();
        } else {
            setError('No verification token provided');
            setLoading(false);
        }
    }, [token]);

    const accountTypeLabel = type === 'client'
        ? 'Client Administrator'
        : type === 'admin'
            ? 'Internal Admin'
            : 'User';

    const passwordRules = checkPasswordRules(password);
    const passwordValid = Object.values(passwordRules).every(Boolean);
    const passwordsMatch = password && confirmPassword && password === confirmPassword;

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        
        if (!passwordValid) {
            setError('Password does not meet the requirements below');
            return;
        }
        
        try {
            setVerifying(true);
            setError('');
            
            const endpoint =
                type === 'admin'  ? '/api/verify/admin'  :
                type === 'client' ? '/api/verify/client' :
                                    '/api/verify/user';
            
            const response = await apiFetchJSON(endpoint, {
                method: 'POST',
                body: JSON.stringify({ token, password })
            });
            
            if (response.success) {
                setSuccess(true);
                setTimeout(() => navigate('/login'), 3000);
            } else {
                setError(response.error || 'Verification failed');
            }
        } catch (err) {
            setError(err.message || 'Verification failed');
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-header">
                        <img src={logo} className="login-logo-247" alt="247 Logo" />
                        <div><h2>Verifying your account</h2></div>
                    </div>
                    <div className="verification-loading">
                        <span className="spinner-icon">↻</span>
                        <p>Please wait...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !success && !tokenValid) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-header">
                        <img src={logo} className="login-logo-247" alt="247 Logo" />
                        <div><h2>Verification Failed</h2></div>
                    </div>
                    
                    <div className="login-error">
                        <span className="login-error-icon">!</span>
                        {error}
                    </div>
                    
                    <button 
                        className="login-btn"
                        onClick={() => navigate('/login')}
                    >
                        Go to Login
                    </button>
                    
                    <div className="login-info">
                        <p>Need help? Contact your administrator</p>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="login-page">
                <div className="login-container">
                    <div className="login-header">
                        <img src={logo} className="login-logo-247" alt="247 Logo" />
                        <div><h2>Account Verified!</h2></div>
                    </div>
                    
                    <div className="verification-success">
                        <svg
                            className="verification-success-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M5 12.5L9.5 17L19 7.5"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <p>Your account has been successfully verified.</p>
                        <p className="redirect-note">Redirecting you to login in 3 seconds...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-container verification-container">
                <div className="login-header">
                    <img src={logo} className="login-logo-247" alt="247 Logo" />
                    <div>
                        <h2 className="verification-title">
                            {type === 'client'
                                ? 'Complete registration'
                                : type === 'admin'
                                    ? 'Set up your admin account'
                                    : 'Set up your account'}
                        </h2>
                        <p>Twenty4Seven Central Monitoring System</p>
                    </div>
                </div>
                
                <div className="verification-data-block">
                    {accountInfo?.username && (
                        <div className="verification-data-row">
                            <span className="verification-data-label">Username</span>
                            <span className="verification-data-value mono">{accountInfo.username}</span>
                        </div>
                    )}
                    <div className="verification-data-row">
                        <span className="verification-data-label">Account Type</span>
                        <span className="verification-data-value">{accountTypeLabel}</span>
                    </div>
                </div>
                
                {error && (
                    <div className="login-error">
                        <span className="login-error-icon">!</span>
                        {error}
                    </div>
                )}
                
                <form onSubmit={handleSubmit}>
                    <div className="form-group password-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={verifying}
                                autoFocus
                            />
                            <button 
                                type="button" 
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={verifying}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                <EyeIcon />
                            </button>
                        </div>
                        <PasswordStrength password={password} />
                    </div>
                    
                    <div className="form-group password-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <div className="password-input-wrapper">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={verifying}
                            />
                            {passwordsMatch && <span className="pw-match-indicator">MATCH ✓</span>}
                            <button 
                                type="button" 
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={verifying}
                                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                            >
                                <EyeIcon />
                            </button>
                        </div>
                        {password && confirmPassword && !passwordsMatch && (
                            <div className="pw-mismatch">Passwords do not match</div>
                        )}
                    </div>
                    
                    <button 
                        type="submit" 
                        className="login-btn"
                        disabled={verifying || !passwordValid || !passwordsMatch}
                    >
                        {verifying ? (
                            <>
                                <span className="spinner-icon">↻</span>
                                Verifying...
                            </>
                        ) : (
                            'Activate account'
                        )}
                    </button>
                </form>
                
                <div className="login-info">
                    <p>This link expires in 24 hours</p>
                </div>
            </div>
        </div>
    );
}

export default ClientVerification;
